/**
 * Cascading Words Animation Module
 * Creates a cascading "enter" effect for word-by-word text animations
 * 
 * @module cascadingWords
 * @example
 * // HTML usage:
 * // <h1 class="cascading-words cascading-words--enter" data-animated>Title…</h1>
 * // Optional attributes: data-interval="100" data-duration="800"
 */

// Constants
const ANIM_ATTR = 'data-animated';
const WORD_CLASS = 'cascading-words__word';
const DEFAULT_INTERVAL = 70; // ms between words (stagger)
const DEFAULT_DURATION = 700; // ms animation per word

/**
 * Splits the text content of an element into individual word spans
 * @private
 * @param {HTMLElement} el - The element containing text to split
 * @returns {NodeList} List of created word elements
 */
function splitToWords(el) {
    if (!el) return [];
    if (el.__cascadingWordsSplit) return el.querySelectorAll(`.${WORD_CLASS}`) || [];
    
    const text = el.textContent;
    el.textContent = '';
    const frag = document.createDocumentFragment();

    // Split by words, preserving multiple spaces
    const words = text.split(/(\s+)/);

    words.forEach(word => {
        const span = document.createElement('span');
        span.className = WORD_CLASS;
        // Preserve spaces visually using NBSP so inline-block spans keep width
        span.textContent = word === ' ' ? '\u00A0' : word;
        frag.appendChild(span);
    });

    el.appendChild(frag);
    el.__cascadingWordsSplit = true;
    return el.querySelectorAll(`.${WORD_CLASS}`);
}

/**
 * Applies the cascading enter animation to an element's words
 * @private
 * @param {HTMLElement} el - The element to animate
 * @param {Object} options - Animation options
 * @param {number} [options.interval=DEFAULT_INTERVAL] - Time between word animations in ms
 * @param {number} [options.duration=DEFAULT_DURATION] - Duration of each word's animation in ms
 */
function applyEnter(el, { interval = DEFAULT_INTERVAL, duration = DEFAULT_DURATION } = {}) {
    const words = splitToWords(el);
    
    // Apply animation properties to each word
    words.forEach((node, i) => {
        node.style.animationDuration = `${duration}ms`;
        node.style.animationDelay = `${interval * (i + 1)}ms`;
    });
    
    // Calculate total animation time
    const total = interval * words.length + duration + 50;
    
    // Set CSS custom property for total duration if needed
    try {
        document.documentElement.style.setProperty('--title-enter-total', `${total}ms`);
    } catch (e) {
        // Silently fail if setting the property fails
        console.debug('Could not set CSS variable --title-enter-total', e);
    }
    
    // Clean up after animation completes
    const cleanup = () => {
        el.removeAttribute(ANIM_ATTR);
        words.forEach(word => {
            word.style.animationDelay = '';
            word.style.animationDuration = '';
        });
        el.removeEventListener('animationend', cleanup);
    };
    
    // Clean up when any word's animation ends
    words[words.length - 1]?.addEventListener('animationend', cleanup, { once: true });
    
    // Fallback cleanup in case animationend doesn't fire
    setTimeout(cleanup, total);
}

/**
 * Initializes the cascading words animation on all matching elements
 * @private
 */
function init() {
    const elements = document.querySelectorAll(`.cascading-words--enter[${ANIM_ATTR}]`);
    elements.forEach(el => {
        applyEnter(el, {
            interval: Number(el.dataset.interval) || DEFAULT_INTERVAL,
            duration: Number(el.dataset.duration) || DEFAULT_DURATION,
        });
    });
}

/**
 * Applies cascading words enter animation to the specified target(s)
 * @param {string|HTMLElement} target - CSS selector or DOM element to animate
 * @param {Object} [opts] - Animation options
 * @param {number} [opts.interval] - Time between word animations in ms
 * @param {number} [opts.duration] - Duration of each word's animation in ms
 * @example
 * import { applyCascadingWordsEnter } from './js/cascading-words.js';
 * applyCascadingWordsEnter('.my-heading', { interval: 100, duration: 800 });
 */
function applyCascadingWordsEnter(target, opts = {}) {
    if (typeof target === 'string') {
        document.querySelectorAll(target).forEach(el => applyEnter(el, opts));
    } else if (target instanceof Element) {
        applyEnter(target, opts);
    }
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    // DOM already loaded, initialize immediately
    init();
}

// Export the public API
export { applyCascadingWordsEnter };
export default applyCascadingWordsEnter;
